import { api } from '../../lib/api.js';
import { BaseComponent } from '../../lib/base-component.js';
import { INVITATION_TYPES, invitationTypeLabel } from '../../lib/invitation-types.js';
import {
    contactPath,
    contactsPath,
    DEFAULT_FILTERS,
    invitableEntry,
    inviteWave,
    isPending,
    LISTS,
    normalizeFilters,
    QUEUE_FILTERS,
    SOURCE_SITES,
    sourceSiteLabel,
    summarizeWave,
} from '../../lib/lists.js';
import { css, html, nothing } from '../../lib/lit.js';

function formatDate(iso) {
    if (!iso) return '';
    const date = new Date(iso);
    return Number.isNaN(date.getTime()) ? iso : date.toLocaleString();
}

const EITHER_OPTIONS = [
    { value: '', label: 'Any' },
    { value: 'true', label: 'Yes' },
    { value: 'false', label: 'No' },
];

// Everyone on d3-community's public lists (early access and the newsletter),
// one row per person with a badge for each list they're on, pending sign-ups
// included. The "Beta queue" preset filters to confirmed early-access sign-ups
// not yet invited, oldest first; staff select people and invite them in a wave —
// the Staff API creates each d3-auth invitation and marks the entry invited. A
// wave goes out in chunks, and each person's outcome is reported. Removing
// someone happens on their own page.
class ListsView extends BaseComponent {
    static properties = {
        _filters: { state: true },
        /** @type {Array<object>} */
        _contacts: { state: true },
        _total: { state: true },
        _nextCursor: { state: true },
        _loading: { state: true },
        _loadingMore: { state: true },
        _error: { state: true },
        /** @type {Set<string>} Early-access entry ids. */
        _selected: { state: true },
        _type: { state: true },
        _confirmingInvite: { state: true },
        _inviting: { state: true },
        /** @type {{done: number, total: number}|null} */
        _progress: { state: true },
        /** @type {{invited: Array<object>, failed: Array<object>, type: string}|null} */
        _wave: { state: true },
    };

    static styles = css`
        :host {
            display: block;
        }
        .wrap {
            max-width: 72rem;
            margin: 0 auto;
            padding: 2rem 1rem 4rem;
        }
        table {
            font-size: 0.9rem;
        }
        .filters .form-select,
        .filters .form-control {
            min-width: 8rem;
        }
    `;

    constructor() {
        super();
        this._filters = { ...DEFAULT_FILTERS };
        this._contacts = [];
        this._total = 0;
        this._nextCursor = '';
        this._loading = true;
        this._loadingMore = false;
        this._error = '';
        this._selected = new Set();
        this._type = INVITATION_TYPES[0].slug;
        this._confirmingInvite = false;
        this._inviting = false;
        this._progress = null;
        this._wave = null;
    }

    connectedCallback() {
        super.connectedCallback();
        this._load();
    }

    async _load() {
        this._loading = true;
        this._error = '';
        this._selected = new Set();
        this._confirmingInvite = false;
        try {
            const page = await api.get(contactsPath(this._filters));
            this._contacts = page.contacts;
            this._total = page.total;
            this._nextCursor = page.nextCursor ?? '';
        } catch (error) {
            this._error = error.message;
        } finally {
            this._loading = false;
        }
    }

    async _loadMore() {
        this._loadingMore = true;
        this._error = '';
        try {
            const page = await api.get(contactsPath(this._filters, this._nextCursor));
            this._contacts = [...this._contacts, ...page.contacts];
            this._total = page.total;
            this._nextCursor = page.nextCursor ?? '';
        } catch (error) {
            this._error = error.message;
        } finally {
            this._loadingMore = false;
        }
    }

    _setFilter(name, value) {
        this._setFilters({ ...this._filters, [name]: value });
    }

    _setFilters(filters) {
        this._filters = normalizeFilters(filters);
        this._load();
    }

    _toggle(id, checked) {
        const selected = new Set(this._selected);
        if (checked) selected.add(id);
        else selected.delete(id);
        this._selected = selected;
        this._confirmingInvite = false;
    }

    _toggleAll(checked) {
        this._selected = checked ? new Set(this._invitableEntries().map((e) => e.id)) : new Set();
        this._confirmingInvite = false;
    }

    _invitableEntries() {
        return this._contacts.map(invitableEntry).filter(Boolean);
    }

    async _invite() {
        this._inviting = true;
        this._wave = null;
        const ids = [...this._selected];
        const type = this._type;
        this._progress = { done: 0, total: ids.length };
        try {
            const results = await inviteWave(
                (path, body) => api.post(path, body),
                type,
                ids,
                (done, total) => {
                    this._progress = { done, total };
                },
            );
            this._wave = { ...summarizeWave(results), type };
        } finally {
            this._inviting = false;
            this._progress = null;
            this._confirmingInvite = false;
        }
        this._load();
    }

    render() {
        return html`
            <div class="wrap">
                <div class="d-flex align-items-center gap-2 mb-2">
                    <h1 class="h4 mb-0">Lists</h1>
                    <button
                        class="btn btn-sm btn-outline-secondary"
                        type="button"
                        ?disabled=${this._loading || this._inviting}
                        @click=${this._load}
                    >
                        Refresh
                    </button>
                </div>
                <p class="text-muted">
                    Everyone who signed up on vtt.dicedriven.com or dicedriven.com, for early access or
                    the newsletter. Pending sign-ups haven't clicked their confirmation link yet. The
                    beta queue is confirmed early access, not yet invited, oldest first.
                </p>
                ${this._renderFilters()} ${this._renderWave()}
                ${
                    this._error
                        ? html`<div class="alert alert-danger" role="alert">${this._error}</div>`
                        : nothing
                }
                ${this._renderInviteBar()} ${this._renderTable()}
            </div>
        `;
    }

    _renderSelect(name, label, options, disabled = false) {
        return html`
            <div>
                <label class="form-label small mb-1" for=${name}>${label}</label>
                <select
                    id=${name}
                    class="form-select form-select-sm"
                    .value=${this._filters[name]}
                    ?disabled=${this._inviting || disabled}
                    @change=${(e) => this._setFilter(name, e.target.value)}
                >
                    ${options.map(
                        (o) =>
                            html`<option value=${o.value} ?selected=${o.value === this._filters[name]}>
                                ${o.label}
                            </option>`,
                    )}
                </select>
            </div>
        `;
    }

    _renderDate(name, label) {
        return html`
            <div>
                <label class="form-label small mb-1" for=${name}>${label}</label>
                <input
                    id=${name}
                    class="form-control form-control-sm"
                    type="date"
                    .value=${this._filters[name]}
                    ?disabled=${this._inviting}
                    @change=${(e) => this._setFilter(name, e.target.value)}
                />
            </div>
        `;
    }

    _renderEmail() {
        return html`
            <div>
                <label class="form-label small mb-1" for="email">Email</label>
                <input
                    id="email"
                    class="form-control form-control-sm"
                    type="search"
                    maxlength="254"
                    placeholder="Part of the address"
                    .value=${this._filters.email}
                    ?disabled=${this._inviting}
                    @change=${(e) => this._setFilter('email', e.target.value)}
                />
            </div>
        `;
    }

    _renderFilters() {
        // GM and invited are early access only; the API refuses them alongside
        // the newsletter.
        const newsletter = this._filters.list === 'newsletter';
        return html`
            <div class="d-flex flex-wrap gap-2 mb-2">
                <button
                    class="btn btn-sm btn-outline-primary"
                    type="button"
                    ?disabled=${this._inviting}
                    @click=${() => this._setFilters({ ...QUEUE_FILTERS })}
                >
                    Beta queue
                </button>
                <button
                    class="btn btn-sm btn-outline-secondary"
                    type="button"
                    ?disabled=${this._inviting}
                    @click=${() => this._setFilters({ ...DEFAULT_FILTERS })}
                >
                    Reset filters
                </button>
            </div>
            <div class="filters d-flex flex-wrap align-items-end gap-3 mb-3">
                ${this._renderSelect('list', 'List', [{ value: '', label: 'Any' }, ...LISTS])}
                ${this._renderSelect('confirmed', 'Confirmed', EITHER_OPTIONS)}
                ${this._renderSelect('gm', 'GM', EITHER_OPTIONS, newsletter)}
                ${this._renderSelect('invited', 'Invited', EITHER_OPTIONS, newsletter)}
                ${this._renderSelect('sourceSite', 'Source', [
                    { value: '', label: 'Any' },
                    ...SOURCE_SITES,
                ])}
                ${this._renderEmail()}
                ${this._renderDate('from', 'Signed up from')} ${this._renderDate('to', 'to')}
                ${this._renderSelect('sort', 'Order', [
                    { value: 'oldest', label: 'Oldest first' },
                    { value: 'newest', label: 'Newest first' },
                ])}
                <div class="ms-auto text-end">
                    <div class="fs-5 fw-semibold">${this._loading ? '…' : this._total}</div>
                    <div class="small text-muted">matching</div>
                </div>
            </div>
        `;
    }

    _renderWave() {
        if (!this._wave) return nothing;
        const { invited, failed, type } = this._wave;
        return html`
            <div class="alert ${failed.length ? 'alert-warning' : 'alert-success'}" role="status">
                <div class="d-flex align-items-start">
                    <div>
                        Invited ${invited.length} as <strong>${invitationTypeLabel(type)}</strong>.
                        ${failed.length ? html`${failed.length} failed:` : nothing}
                    </div>
                    <button
                        type="button"
                        class="btn-close ms-auto"
                        aria-label="Dismiss"
                        @click=${() => {
                            this._wave = null;
                        }}
                    ></button>
                </div>
                ${
                    failed.length
                        ? html`<ul class="mb-0 mt-2">
                              ${failed.map(
                                  (r) =>
                                      html`<li>
                                          ${
                                              r.contactId
                                                  ? html`<a href=${contactPath(r.contactId)}>${r.email}</a>`
                                                  : r.email || r.entryId
                                          }:
                                          ${r.detail}
                                      </li>`,
                              )}
                          </ul>`
                        : nothing
                }
            </div>
        `;
    }

    _renderInviteBar() {
        const count = this._selected.size;
        const selectedType = INVITATION_TYPES.find((t) => t.slug === this._type);

        let action;
        if (this._inviting) {
            action = html`<span class="small"
                >Inviting… ${this._progress?.done ?? 0} of ${this._progress?.total ?? count}</span
            >`;
        } else if (this._confirmingInvite) {
            action = html`
                <span class="small">
                    Invite ${count} ${count === 1 ? 'person' : 'people'} as
                    <strong>${selectedType?.label}</strong>? d3-auth emails each of them.
                </span>
                <button class="btn btn-sm btn-primary" type="button" @click=${this._invite}>
                    Invite now
                </button>
                <button
                    class="btn btn-sm btn-outline-secondary"
                    type="button"
                    @click=${() => {
                        this._confirmingInvite = false;
                    }}
                >
                    Cancel
                </button>
            `;
        } else {
            action = html`<button
                class="btn btn-sm btn-primary"
                type="button"
                ?disabled=${count === 0}
                @click=${() => {
                    this._confirmingInvite = true;
                }}
            >
                Invite ${count} selected
            </button>`;
        }

        return html`
            <div class="d-flex flex-wrap align-items-center gap-2 mb-3 p-2 border rounded bg-body-tertiary">
                <label class="small" for="type">Invite as</label>
                <select
                    id="type"
                    class="form-select form-select-sm w-auto"
                    .value=${this._type}
                    ?disabled=${this._inviting || this._confirmingInvite}
                    @change=${(e) => {
                        this._type = e.target.value;
                    }}
                >
                    ${INVITATION_TYPES.map(
                        (t) => html`<option value=${t.slug} ?selected=${t.slug === this._type}>
                            ${t.label}
                        </option>`,
                    )}
                </select>
                <span class="small text-muted">${selectedType?.hint}</span>
                <div class="ms-auto d-flex align-items-center gap-2">${action}</div>
            </div>
        `;
    }

    _renderTable() {
        if (this._loading && this._contacts.length === 0) {
            return html`<p class="text-muted">Loading…</p>`;
        }
        if (this._contacts.length === 0) {
            return html`<p class="text-muted">Nobody matches these filters.</p>`;
        }

        const invitable = this._invitableEntries();
        const allSelected =
            invitable.length > 0 && invitable.every((e) => this._selected.has(e.id));
        return html`
            <div class="table-responsive">
                <table class="table table-sm table-hover align-middle">
                    <thead>
                        <tr>
                            <th>
                                <input
                                    class="form-check-input"
                                    type="checkbox"
                                    aria-label="Select everyone loaded who can be invited"
                                    .checked=${allSelected}
                                    ?disabled=${this._inviting || invitable.length === 0}
                                    @change=${(e) => this._toggleAll(e.target.checked)}
                                />
                            </th>
                            <th>Email</th>
                            <th>Lists</th>
                            <th>Source</th>
                            <th>UTM source</th>
                            <th>Signed up</th>
                        </tr>
                    </thead>
                    <tbody>
                        ${this._contacts.map((contact) => this._renderRow(contact))}
                    </tbody>
                </table>
            </div>
            <div class="d-flex align-items-center gap-2">
                <span class="small text-muted">Showing ${this._contacts.length} of ${this._total}</span>
                ${
                    this._nextCursor
                        ? html`<button
                              class="btn btn-sm btn-outline-secondary"
                              type="button"
                              ?disabled=${this._loadingMore || this._inviting}
                              @click=${this._loadMore}
                          >
                              ${this._loadingMore ? 'Loading…' : 'Load more'}
                          </button>`
                        : nothing
                }
            </div>
        `;
    }

    /**
     * The entry this row's details come from: the filtered list's, else the
     * earliest sign-up.
     */
    _primaryEntry(contact) {
        const entries = LISTS.map((l) => contact.lists[l.value]).filter(Boolean);
        if (this._filters.list && contact.lists[this._filters.list]) {
            return contact.lists[this._filters.list];
        }
        return entries.sort((a, b) => a.createdAt.localeCompare(b.createdAt))[0];
    }

    _renderBadges(contact) {
        const earlyAccess = contact.lists['early-access'];
        const newsletter = contact.lists.newsletter;
        const pending = (entry) =>
            isPending(entry)
                ? html`<span class="badge text-bg-warning ms-1" title="Hasn't confirmed yet">pending</span>`
                : nothing;
        return html`
            ${
                earlyAccess
                    ? html`<span class="text-nowrap me-2">
                          <span class="badge text-bg-primary">Early access</span>${pending(earlyAccess)}
                          ${earlyAccess.gm ? html`<span class="badge text-bg-info">GM</span>` : nothing}
                          ${
                              earlyAccess.invitedAt
                                  ? html`<span class="badge text-bg-secondary" title=${formatDate(earlyAccess.invitedAt)}
                                        >invited</span
                                    >`
                                  : nothing
                          }
                      </span>`
                    : nothing
            }
            ${
                newsletter
                    ? html`<span class="text-nowrap">
                          <span class="badge text-bg-dark">Newsletter</span>${pending(newsletter)}
                      </span>`
                    : nothing
            }
        `;
    }

    _renderRow(contact) {
        const entry = invitableEntry(contact);
        const primary = this._primaryEntry(contact);
        return html`
            <tr>
                <td>
                    <input
                        class="form-check-input"
                        type="checkbox"
                        aria-label="Select ${contact.email}"
                        .checked=${entry ? this._selected.has(entry.id) : false}
                        ?disabled=${!entry || this._inviting}
                        title=${entry ? '' : 'Only confirmed early-access sign-ups not yet invited'}
                        @change=${(e) => this._toggle(entry.id, e.target.checked)}
                    />
                </td>
                <td><a href=${contactPath(contact.id)}>${contact.email}</a></td>
                <td>${this._renderBadges(contact)}</td>
                <td class="text-muted">${sourceSiteLabel(primary?.sourceSite ?? '')}</td>
                <td class="text-muted">${primary?.utm?.source ?? ''}</td>
                <td class="text-muted">${formatDate(primary?.createdAt ?? contact.createdAt)}</td>
            </tr>
        `;
    }
}

customElements.define('d3-lists-view', ListsView);
